// Homepage hero search bar - multi-brand + multi-model + body picker.
// Submits to /cars/ (catalog) with all selected fields as query params; the
// catalog filter-panel reads them on load.
//
// All data is sourced from window.AP_DATA (server-localized) and REST:
//   - brand options:   AP_DATA.brands
//   - body options:    AP_DATA.bodies
//   - model options:   GET /wp-json/autoplaza/v1/models?brand=…
//   - live count:      GET /wp-json/autoplaza/v1/listings?…&per_page=1 → total
import { qs, qsa, on } from '../lib/dom.js';
import { AP_DATA } from '../data/listings.js';
import { num } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { getMultiselect, createMultiselect } from './multiselect.js';

function brandOptions() {
  return (AP_DATA.brands || []).map((b) => ({ value: b.name, label: b.name }));
}

function bodyOptions() {
  return (AP_DATA.bodies || []).map((b) => ({ value: b.key, label: b.name }));
}

// Cache of model lists keyed by brand name AND the narrowing context - the same
// brand yields a different list once another filter changes, so the context has
// to be part of the key or a stale list gets replayed.
const modelCache = new Map();

// `narrow` carries every OTHER active filter (here: caroserie). The model list
// must respect it: with Sedan picked, BMW offers 320d and not X3/X5, which the
// filter excludes and which landed the visitor on an empty catalog when tapped.
async function fetchModels(brands, narrow) {
  if (!AP_DATA.apiBase || brands.length === 0) return [];
  const narrowQs = narrow ? narrow.toString() : '';
  const opts = [];
  for (const brand of brands) {
    const key = `${brand}|${narrowQs}`;
    let list;
    if (modelCache.has(key)) {
      list = modelCache.get(key);
    } else {
      try {
        const qs = new URLSearchParams(narrowQs);
        qs.set('brand', brand);
        if (AP_DATA.lang) qs.set('lang', AP_DATA.lang);
        const res = await fetch(`${AP_DATA.apiBase}models?${qs}`, {
          headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
          credentials: 'same-origin',
        });
        list = res.ok ? await res.json() : [];
      } catch { list = []; }
      modelCache.set(key, list);
    }
    // Value = the term SLUG, never the name: a model's slug can differ from
    // its name ("Sealion 06" → sea-lion-06), and slugifying the name built
    // catalog URLs / counts for a model that doesn't exist.
    if (brands.length === 1) {
      list.forEach((m) => opts.push({ value: m.slug || m.name, label: m.name }));
    } else {
      list.forEach((m) => opts.push({ group: brand, value: m.slug || m.name, label: m.name }));
    }
  }
  return opts;
}

// Cache of body lists keyed by sorted brand-list string.
const bodyCache = new Map();

async function fetchBodiesForBrands(brands) {
  if (!AP_DATA.apiBase) return [];
  const key = [...brands].sort().join(',');
  if (bodyCache.has(key)) return bodyCache.get(key);
  let list = [];
  try {
    const bcParams = new URLSearchParams();
    if (key) bcParams.set('brand', key);
    // Language-scope the body counts + RU labels/URLs so the RU search bar never
    // falls back to the RO set when the Referer is dropped.
    if (AP_DATA.lang) bcParams.set('lang', AP_DATA.lang);
    const bcQs = bcParams.toString();
    const url = `${AP_DATA.apiBase}body-counts${bcQs ? `?${bcQs}` : ''}`;
    const res = await fetch(url, {
      headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
      credentials: 'same-origin',
    });
    list = res.ok ? await res.json() : [];
  } catch { list = []; }
  bodyCache.set(key, list);
  return list;
}

export function initSearchBar() {
  const form = qs('[data-component="search-bar"]');
  if (!form) return;

  const brandRoot = qs('[data-role="search-brand"]', form);
  const modelRoot = qs('[data-role="search-model"]', form);
  const bodyRoot = qs('[data-role="search-body"]', form);
  if (!brandRoot || !modelRoot) return;

  const brand = getMultiselect(brandRoot) || createMultiselect(brandRoot);
  const model = getMultiselect(modelRoot) || createMultiselect(modelRoot);
  const body = bodyRoot ? (getMultiselect(bodyRoot) || createMultiselect(bodyRoot)) : null;

  brand.setOptions(brandOptions());
  if (body) body.setOptions(bodyOptions());

  async function syncModelOptions() {
    const brands = brand.getSelected();
    if (brands.length === 0) {
      model.setOptions([]);
      model.setEnabled(false);
      model.setPlaceholder(t('pick_brand_first', 'Alege marca întâi'));
      return;
    }
    model.setEnabled(true);
    model.setPlaceholder(t('loading', 'Se încarcă…'));
    const opts = await fetchModels(brands, searchNarrowParams());
    model.setOptions(opts);
    // Drop model picks the narrowed context no longer offers, the same way
    // syncBodyOptions() does for caroserii - otherwise the count keeps
    // filtering by a dead-end value and the button promises 0 cars.
    const allowed = new Set(opts.map((o) => o.value));
    const kept = model.getSelected().filter((v) => allowed.has(v));
    if (kept.length !== model.getSelected().length) model.setSelected(kept);
    model.setPlaceholder(opts.length ? t('any_model', 'Orice model') : t('no_model', 'Niciun model disponibil'));
  }

  // The filters that narrow the model list. Only caroserie exists in this bar;
  // the key names match atpl_rest_filter_input() so client and server narrow by
  // the same thing.
  function searchNarrowParams() {
    const p = new URLSearchParams();
    const bodies = body ? body.getSelected() : [];
    if (bodies.length) p.set('body', bodies.join(','));
    p.sort();
    return p;
  }

  // Refetch body options scoped to the currently-selected brands so the
  // dropdown only ever offers caroserii that actually have stock for the
  // brand. With no brand selected, fall back to the server-localized
  // global list (AP_DATA.bodies) - already pre-filtered to count > 0.
  async function syncBodyOptions() {
    if (!body) return;
    const brands = brand.getSelected();
    if (brands.length === 0) {
      body.setOptions(bodyOptions());
      body.setPlaceholder(t('any_body', 'Orice caroserie'));
      return;
    }
    body.setPlaceholder(t('loading', 'Se încarcă…'));
    const list = await fetchBodiesForBrands(brands);
    const opts = (list || []).map((b) => ({ value: b.key, label: b.name }));
    body.setOptions(opts);
    // Drop body picks that the new brand context no longer offers so the
    // count widget doesn't keep filtering by a dead-end value.
    const allowed = new Set(opts.map((o) => o.value));
    const kept = body.getSelected().filter((v) => allowed.has(v));
    if (kept.length !== body.getSelected().length) body.setSelected(kept);
    body.setPlaceholder(opts.length ? t('any_body', 'Orice caroserie') : t('no_body', 'Nicio caroserie disponibilă'));
  }

  // Live count of matching listings - REST round-trip on any change.
  const countEl = qs('[data-role="search-count"]', form);
  let countToken = 0;
  let countTimer = 0;
  async function recount() {
    if (!AP_DATA.apiBase || !countEl) return;
    if (countTimer) clearTimeout(countTimer);
    countTimer = setTimeout(async () => {
      const token = ++countToken;
      const params = new URLSearchParams();
      // Comma-joined multi-value params - WP_REST_Request reads through PHP
      // $_GET, which only keeps the LAST value for repeated keys; comma keeps
      // all selections and `$multi()` in inc/rest-api.php splits on comma.
      const setMulti = (key, arr) => { if (arr && arr.length) params.set(key, arr.join(',')); };
      setMulti('brand', brand.getSelected());
      setMulti('model', model.getSelected());
      if (body) setMulti('body', body.getSelected());
      params.set('per_page', '1');
      // Send the page language explicitly so the live count is scoped to the
      // same language as the catalog it links to. The REST endpoint runs on the
      // language-neutral /wp-json/ base and can only otherwise guess via Referer
      // (dropped by privacy settings/proxies), which silently returns RO on RU.
      if (AP_DATA.lang) params.set('lang', AP_DATA.lang);
      try {
        const res = await fetch(`${AP_DATA.apiBase}listings?${params}`, {
          headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (token !== countToken) return;
        countEl.textContent = num(data.total || 0);
      } catch { }
    }, 200);
  }

  on(brandRoot, 'multiselect:change', async () => {
    // Bodies first: it can drop caroserie picks the new brand doesn't offer,
    // and the model list is narrowed by whatever survives that.
    await syncBodyOptions();
    await syncModelOptions();
    recount();
  });
  on(modelRoot, 'multiselect:change', recount);
  if (bodyRoot) {
    on(bodyRoot, 'multiselect:change', () => {
      recount();
      // The model list depends on the caroserie too - refresh it, then recount
      // in case a now-impossible model pick was dropped.
      syncModelOptions().then(recount);
    });
  }

  qsa('input, select', form).forEach((el) => {
    on(el, 'input', recount);
    on(el, 'change', recount);
  });

  syncModelOptions();
  // No initial recount - the count was server-rendered.

  // On submit, redirect to the canonical pretty URL:
  //   /catalog/{manufacturer-slugs}/{model-X-or-Y}/{body-suv}/
  //
  // Default browser GET submission concatenates each multi-select value as a
  // repeated query param (`?brand=BMW&brand=Audi`), which PHP's `$_GET`
  // collapses to the last value - losing all earlier selections. Building
  // the URL by hand with `-or-` joiners (per the SEO audit, line 667/671)
  // keeps every selection and produces the indexable canonical form.
  on(form, 'submit', (e) => {
    e.preventDefault();
    const fd = new FormData(form);

    // Triangulate the selected values from THREE independent sources:
    //   1. the multiselect instance's `getSelected()`
    //   2. the form's hidden inputs (FormData)
    //   3. any checked checkboxes currently in the DOM (in the portaled panel)
    //
    // We union them so even if one source is stale (e.g. the panel hasn't
    // re-emitted its state after a transition, or a close() handler raced
    // with submit), we still capture every brand the user actually ticked.
    const checked = (root) => {
      if (!root) return [];
      // Look inside the multiselect's portaled panel - checkboxes live there.
      // The panel reference is stored on root itself when createMultiselect
      // ran. As a fallback, scan document.body for the panel by its anchor.
      const panel = root._atplPanel || document.querySelector(`[data-role="ms-panel"][data-ms-host="${root.id || ''}"]`) || null;
      // Generic fallback: any checked checkbox in any open multiselect panel.
      const scope = panel || document;
      return Array.from(scope.querySelectorAll('input[type="checkbox"]:checked'))
        .map((cb) => cb.value)
        .filter(Boolean);
    };

    const merge = (...arrs) => {
      const out = new Set();
      arrs.forEach((arr) => (arr || []).forEach((v) => { if (v) out.add(v); }));
      return Array.from(out);
    };

    const brands = merge(brand.getSelected(), fd.getAll('brand'));
    const models = merge(modelRoot ? model.getSelected() : [], fd.getAll('model'));
    const bodies = merge(body ? body.getSelected() : [], fd.getAll('body'));

    const brandSlug = (name) => {
      const hit = (AP_DATA.brands || []).find((b) => b.name === name);
      if (hit && hit.slug) return hit.slug;
      return atplSlug(name);
    };
    const bodySlug = (val) => {
      const hit = (AP_DATA.bodies || []).find((b) => b.key === val || b.name === val);
      return hit ? hit.key : atplSlug(val);
    };

    let base = AP_DATA.catalog || '/catalog/';
    if (!base.endsWith('/')) base += '/';

    const segments = [];

    if (brands.length) {
      segments.push(brands.map(brandSlug).join('-or-'));
    }
    if (models.length) {
      segments.push('model-' + models.map(atplSlug).join('-or-'));
    }
    if (bodies.length) {
      segments.push('body-' + bodies.map(bodySlug).join('-or-'));
    }

    const path = base + (segments.length ? segments.join('/') + '/' : '');
    window.location.assign(path);
  });
}

// Slug helper - keep in sync with `atplSlug` in components/filter-panel.js
// and PHP's sanitize_title(). Lowercases, strips diacritics, replaces
// non-alphanumerics with '-'.
function atplSlug(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
